import React from 'react';
import type { IntegrationAddress } from 'src/api/generated';
import { PiPhone, PiShieldCheck, PiStar } from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tag from 'src/modules/shared/components/atoms/tag';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { isRecord } from 'src/modules/shared/utils/types.util';
import { formatPhoneNumber } from 'src/modules/shared/utils/formatting/phone.util';
import {
  getVerificationLabel,
  getVerificationVariant,
  getQualityLabel,
  getQualityVariant,
} from '../../../utils/whatsapp-phone-presentation.util';

interface WhatsAppPhoneRowProps {
  /** Persisted account phone shown by this row. */
  phone: IntegrationAddress;
}

/** Presents a phone and its Meta verification and quality indicators. */
export const WhatsAppPhoneRow: React.FC<WhatsAppPhoneRowProps> = ({
  phone,
}) => {
  const { t } = useI18n();
  const metadata = isRecord(phone.metadata) ? phone.metadata : {};
  const verificationStatus =
    typeof metadata.code_verification_status === 'string'
      ? metadata.code_verification_status
      : 'UNKNOWN';
  const qualityRating =
    typeof metadata.quality_rating === 'string'
      ? metadata.quality_rating
      : 'UNKNOWN';
  const formattedPhoneNumber = formatPhoneNumber(phone.address);
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-secondary-100 bg-secondary-50/60 px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <PiPhone className="h-4 w-4 text-secondary-500 shrink-0" />
        <div className="min-w-0">
          <Text size="small" weight="semibold">
            {formattedPhoneNumber}
          </Text>
          <Text size="tiny" color="secondary">
            {t('settings.integrations.whatsapp.accounts.identifier', {
              id: phone.providerId,
            })}
          </Text>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap justify-end">
        <Tag
          content={t(getVerificationLabel(verificationStatus))}
          variant={getVerificationVariant(verificationStatus)}
          appearance="soft"
          size="2xs"
          icon={<PiShieldCheck className="h-3 w-3" />}
          iconPosition="left"
        />

        <Tag
          content={t(getQualityLabel(qualityRating))}
          variant={getQualityVariant(qualityRating)}
          appearance="soft"
          size="2xs"
          icon={<PiStar className="h-3 w-3" />}
          iconPosition="left"
        />
      </div>
      {phone.syncStatus === 'contact_sync_failed' ||
      phone.syncStatus === 'history_sync_failed' ? (
        <div className="basis-full" role="note">
          <Text size="small" color="secondary">
            {t('settings.integrations.whatsapp.recovery.importWarning')}
          </Text>
        </div>
      ) : null}
    </div>
  );
};
